export type BookingWidgetMode = 'upcoming' | 'repeat'

export type BookingWidgetState = {
  mode: BookingWidgetMode | null
  booking: Booking | null
  daysAgo: number | null
}

type BookingWidgetRawData = {
  active: Booking[]
  past: Booking[]
}

const EMPTY_WIDGET_STATE: BookingWidgetState = {
  mode: null,
  booking: null,
  daysAgo: null
}

function findUpcomingBooking(bookings: Booking[], now = Date.now()): Booking | null {
  const upcoming = bookings.filter(booking => isBookingUpcoming(booking, now))
  if (!upcoming.length) return null
  const next = [...upcoming].sort((a, b) => bookingTimestamp(a) - bookingTimestamp(b))[0] ?? null
  if (!next || !isBookingUpcoming(next, now)) return null
  return next
}

function dedupeBookingsByReference(bookings: Booking[]): Booking[] {
  const seen = new Set<string>()
  const unique: Booking[] = []
  for (const booking of bookings) {
    if (seen.has(booking.reference)) continue
    seen.add(booking.reference)
    unique.push(booking)
  }
  return unique
}

function findRecentRepeatableBooking(bookings: Booking[], now = Date.now()): { booking: Booking, daysAgo: number } | null {
  const candidates = bookings
    .filter(booking => isBookingWithinRepeatWindow(booking, REPEAT_BOOKING_WINDOW_DAYS, now))
    .sort((a, b) => bookingTimestamp(b) - bookingTimestamp(a))

  const booking = candidates[0]
  if (!booking) return null

  return {
    booking,
    daysAgo: daysSinceBooking(booking, now)
  }
}

function resolveBookingWidgetState(
  activeBookings: Booking[],
  pastBookings: Booking[],
  now = Date.now()
): BookingWidgetState {
  const upcoming = findUpcomingBooking(activeBookings, now)
  if (upcoming && !isBookingCancelled(upcoming)) {
    return {
      mode: 'upcoming',
      booking: upcoming,
      daysAgo: null
    }
  }

  const completedFromActive = activeBookings.filter(booking => isBookingCompleted(booking, now))
  const repeatCandidates = dedupeBookingsByReference([
    ...pastBookings,
    ...completedFromActive
  ])

  const recent = findRecentRepeatableBooking(repeatCandidates, now)
  if (recent) {
    return {
      mode: 'repeat',
      booking: recent.booking,
      daysAgo: recent.daysAgo
    }
  }

  return EMPTY_WIDGET_STATE
}

function fetchUserBookings(userToken: string, userLocale: string) {
  const baseUrl = useBaseUrl()
  const headers = {
    'Content-Type': 'application/json',
    'User-token': userToken
  }
  const fetchOptions = {
    method: 'GET' as const,
    headers,
    cache: 'no-store' as const
  }

  return Promise.all([
    $fetch<{ data?: Booking[] }>(`${baseUrl}/active-reservations`, {
      ...fetchOptions,
      query: {
        locale: userLocale,
        limit: 20,
        page: 1
      }
    }),
    $fetch<{ data?: Booking[] }>(`${baseUrl}/past-reservations`, {
      ...fetchOptions,
      query: {
        locale: userLocale,
        limit: 20,
        page: 1
      }
    })
  ]).then(([activeRes, pastRes]) => ({
    active: activeRes?.data ?? [],
    past: pastRes?.data ?? []
  }))
}

/**
 * Widget unificado de reserva para header y home.
 * Prioridad: próxima reserva activa > repetir reserva reciente (últimos 14 días).
 * Solo hace fetch en la home (el widget no se muestra en otras rutas).
 */
export function useBookingWidget() {
  const { locale } = useI18n()
  const { isHome } = useIsHome()
  const { loggedIn } = useUserSession()
  const token = useCookie('byhours_connect')
  const { getRepeatBookingUrl } = useRepeatBooking()

  const noCacheFetchOptions = useNoCacheFetchOptions()

  const { data, status, refresh } = useLazyAsyncData<BookingWidgetRawData>(
    'booking-widget',
    async () => {
      if (!isHome.value || !loggedIn.value || !token.value) {
        return { active: [], past: [] }
      }

      return fetchUserBookings(token.value, locale.value)
    },
    {
      server: false,
      immediate: import.meta.client && loggedIn.value && isHome.value,
      default: () => ({ active: [], past: [] }),
      watch: [isHome, loggedIn, token, locale],
      ...noCacheFetchOptions
    }
  )

  const widgetState = computed<BookingWidgetState>(() => {
    if (!isHome.value) return EMPTY_WIDGET_STATE
    const raw = data.value
    if (!raw) return EMPTY_WIDGET_STATE
    return resolveBookingWidgetState(raw.active, raw.past)
  })

  const mode = computed(() => widgetState.value.mode)
  const booking = computed(() => widgetState.value.booking)
  const daysAgo = computed(() => widgetState.value.daysAgo)
  const hasBookingWidget = computed(() => mode.value !== null && booking.value !== null)
  const isLoading = computed(() => status.value === 'pending')

  const repeatUrl = computed(() => {
    if (mode.value !== 'repeat' || !booking.value) return null
    return getRepeatBookingUrl(booking.value)
  })

  const bookingsLink = computed(() => `/${locale.value}/user-zone/bookings`)

  /** Compatibilidad con código que solo necesita la próxima reserva activa. */
  const nextBooking = computed(() => (mode.value === 'upcoming' ? booking.value : null))
  const hasNextBooking = computed(() => nextBooking.value !== null)

  return {
    mode,
    booking,
    daysAgo,
    hasBookingWidget,
    repeatUrl,
    bookingsLink,
    isLoading,
    status,
    refresh,
    nextBooking,
    hasNextBooking
  }
}
